import { apiUrl } from './apiConfig';
import { getToken } from './auth';

const STORAGE_KEY = 'kkt_notifications';

function authHeaders() {
  const token = getToken();
  if (!token || token === 'undefined') return null;
  return { Authorization: `Bearer ${token}` };
}

function emit() {
  try {
    window.dispatchEvent(new Event('kkt-notifications'));
  } catch {
    /* ignore */
  }
}

export function loadNotifications() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function saveNotifications(list) {
  const items = (Array.isArray(list) ? list : []).slice(0, 40);
  if (!items.length) {
    localStorage.removeItem(STORAGE_KEY);
    return;
  }
  localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
}

export function unreadCount(list) {
  return list.filter((n) => !n.read).length;
}

async function postRead(body) {
  const headers = authHeaders();
  if (!headers) return;
  try {
    await fetch(apiUrl('/api/notifications/read'), {
      method: 'POST',
      headers: { ...headers, 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
  } catch {
    /* offline — пометка останется локально */
  }
}

export function markAllRead(list) {
  const next = (list || []).map((n) => ({ ...n, read: true }));
  saveNotifications(next);
  postRead({ all: true });
  emit();
  return next;
}

export function markRead(list, id) {
  const next = (list || []).map((n) =>
    String(n.id) === String(id) ? { ...n, read: true } : n
  );
  saveNotifications(next);
  postRead({ id });
  emit();
  return next;
}

export function clearNotifications() {
  saveNotifications([]);
  postRead({ clear: true });
  emit();
  return [];
}

export async function fetchServerNotifications() {
  const headers = authHeaders();
  if (!headers) return null;
  const res = await fetch(apiUrl('/api/notifications'), { headers });
  if (res.status === 401 || res.status === 403) return null;
  if (!res.ok) throw new Error('notifications');
  const data = await res.json();
  return Array.isArray(data) ? data : [];
}

/** Лёгкий чекер: unread + meeting (если есть), без полного списка. */
export async function peekServerNotifications() {
  const headers = authHeaders();
  if (!headers) return null;
  const res = await fetch(apiUrl('/api/notifications?peek=1'), { headers });
  if (res.status === 401 || res.status === 403) return null;
  if (!res.ok) throw new Error('notifications-peek');
  const data = await res.json();
  if (!data || typeof data !== 'object' || Array.isArray(data)) return null;
  return data;
}

function upsertNotice(list, notice) {
  if (!notice || notice.id == null) return list || [];
  const id = String(notice.id);
  const next = (list || []).filter((n) => String(n.id) !== id);
  next.unshift(notice);
  return next.slice(0, 40);
}

/**
 * Полный список — при старте и если счётчик разошёлся.
 * Peek каждые ~15 с: сразу открывает совещание, если пришло.
 */
export function startNotificationSync(onChange, intervalMs = 15000) {
  let stopped = false;

  const publish = (list) => {
    saveNotifications(list);
    onChange?.(list);
    emit();
  };

  const full = async () => {
    if (stopped || (typeof document !== 'undefined' && document.hidden)) return;
    try {
      const list = await fetchServerNotifications();
      if (!list) return;
      publish(list);
    } catch {
      /* сеть недоступна — оставляем кэш */
    }
  };

  const peek = async () => {
    if (stopped || (typeof document !== 'undefined' && document.hidden)) return;
    try {
      const data = await peekServerNotifications();
      if (!data) return;
      let list = loadNotifications();
      const localUnread = unreadCount(list);
      if (data.meeting && !data.meeting.read) {
        list = upsertNotice(list, data.meeting);
        publish(list);
        return;
      }
      // Диалог уже прочитан локально, но счётчик разошёлся — подтянуть полный inbox
      if (typeof data.unread === 'number' && data.unread !== localUnread) {
        await full();
      }
    } catch {
      /* ignore */
    }
  };

  full();
  window.setTimeout(peek, 400);
  window.setTimeout(peek, 1200);
  window.setTimeout(peek, 2800);
  const id = setInterval(peek, intervalMs);
  const onVis = () => {
    if (!document.hidden) {
      peek();
      full();
    }
  };
  const onFocus = () => {
    if (!document.hidden) peek();
  };
  document.addEventListener('visibilitychange', onVis);
  window.addEventListener('focus', onFocus);
  return () => {
    stopped = true;
    clearInterval(id);
    document.removeEventListener('visibilitychange', onVis);
    window.removeEventListener('focus', onFocus);
  };
}

export function formatNotifTime(ts) {
  const d = new Date(ts);
  const now = new Date();
  const sameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  if (sameDay) {
    return d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
  }
  return d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' });
}

const isTrustedNoticeHost = (hostname) => {
  const h = String(hostname || '').toLowerCase();
  if (!h) return false;
  if (h === 'localhost' || h === '127.0.0.1' || h === '[::1]' || h === '::1') return true;
  if (h === 'kktbel.ru' || h.endsWith('.kktbel.ru')) return true;
  if (h.endsWith('.cloudpub.ru')) return true;
  return false;
};

export function openNoticeLink(link, eoBase) {
  const href = String(link || '').trim();
  if (!href || href === '#') return;
  if (/^https?:\/\//i.test(href)) {
    try {
      if (!isTrustedNoticeHost(new URL(href).hostname)) return;
    } catch {
      return;
    }
    window.open(href, '_blank', 'noopener');
    return;
  }
  if (href.startsWith('/') && !href.startsWith('//')) {
    const base = String(eoBase || '').replace(/\/$/, '');
    window.open(`${base}${href}`, '_blank', 'noopener');
  }
}

/** Картинка уведа: абсолютный URL на хост API/ЭО (относительный /media с schedule.* не грузится). */
export function resolveNoticeImageUrl(url, eoBase) {
  const raw = String(url || '').trim();
  if (!raw) return '';
  if (/^https?:\/\//i.test(raw)) return raw;
  if (raw.startsWith('//')) {
    if (typeof window !== 'undefined' && window.location?.protocol) {
      return `${window.location.protocol}${raw}`;
    }
    return `https:${raw}`;
  }
  const path = raw.startsWith('/') ? raw : `/${raw}`;
  // Как баннеры: apiUrl → хост ЭО, где лежит /media/
  try {
    const viaApi = apiUrl(path);
    if (viaApi && /^https?:\/\//i.test(viaApi)) return viaApi;
  } catch {
    /* ignore */
  }
  const base = String(eoBase || '').replace(/\/$/, '');
  return base ? `${base}${path}` : path;
}

const NOTICE_ICONS = {
  new_test: 'bi-file-earmark-check',
  new_lecture: 'bi-journal-text',
  new_exam: 'bi-ticket-perforated',
  new_homework: 'bi-pencil-square',
  deadline: 'bi-alarm',
  exam_graded: 'bi-patch-check',
  new_submission: 'bi-inbox',
  homework_graded: 'bi-check2-circle',
  low_result: 'bi-exclamation-triangle-fill',
  group_complete: 'bi-people',
  chat_mute: 'bi-slash-circle',
  login: 'bi-shield-lock',
  warning: 'bi-exclamation-triangle-fill',
  info: 'bi-bell',
  meeting: 'bi-person-workspace',
  parent_meeting: 'bi-people-fill',
  announcement: 'bi-megaphone-fill',
};

export function noticeLevelFromType(type) {
  if (type === 'deadline' || type === 'low_result' || type === 'warning') return 'warning';
  if (
    type === 'exam_graded' ||
    type === 'group_complete' ||
    type === 'new_lecture' ||
    type === 'homework_graded'
  ) {
    return 'success';
  }
  if (type === 'error') return 'error';
  return 'info';
}

export function noticeToneClass(level, type) {
  const lv = level || noticeLevelFromType(type);
  if (lv === 'warning' || lv === 'warn') return 'kkt-notice--warn';
  if (lv === 'success' || lv === 'ok') return 'kkt-notice--ok';
  if (lv === 'error' || lv === 'danger') return 'kkt-notice--error';
  return 'kkt-notice--info';
}

export function noticeIconClass(icon, type, level) {
  const raw = String(icon || '').trim();
  if (raw) return raw.startsWith('bi-') || raw.startsWith('bi ') ? raw.replace(/^bi\s+/, '') : `bi-${raw}`;
  if (NOTICE_ICONS[type]) return NOTICE_ICONS[type];
  const lv = level || noticeLevelFromType(type);
  if (lv === 'warning') return 'bi-exclamation-triangle-fill';
  if (lv === 'success') return 'bi-check-lg';
  if (lv === 'error') return 'bi-exclamation-lg';
  return 'bi-bell';
}
